import { Box } from "@mui/system";
import { useState } from "react";
import { Chart, Interval, Axis, Tooltip, Coordinate, Legend } from "bizcharts";
import DataSet from "@antv/data-set";

import DownloadButton from "../Common/DownloadButton";

const DodgeBarChart = (props) => {
  // New hard-coded data for this year
  const mapped = [
    {
      "Organic flag": "Conventional",
      "Food commodity": "Animal products",
      "% between LOQ and LL samples": 9.24,
      "% Above LL samples": 0.78,
      Conventional: 9.24,
    },
    {
      "Organic flag": "Organic",
      "Food commodity": "Animal products",
      "% between LOQ and LL samples": 7.86,
      "% Above LL samples": 1.31,
      Organic: 7.86,
    },
    {
      "Organic flag": "Conventional",
      "Food commodity": "Cereals",
      "% between LOQ and LL samples": 28.08,
      "% Above LL samples": 6.30,
      Conventional: 28.08,
    },
    {
      "Organic flag": "Organic",
      "Food commodity": "Cereals",
      "% between LOQ and LL samples": 21.20,
      "% Above LL samples": 0.80,
      Organic: 21.20,
    },
    {
      "Organic flag": "Conventional",
      "Food commodity": "Food for infants and young children",
      "% between LOQ and LL samples": 4.86,
      "% Above LL samples": 3.70,
      Conventional: 4.86,
    },
    {
      "Organic flag": "Organic",
      "Food commodity": "Food for infants and young children",
      "% between LOQ and LL samples": 6.91,
      "% Above LL samples": 2.09,
      Organic: 6.91,
    },
    {
      "Organic flag": "Conventional",
      "Food commodity": "Fruits and tree nuts",
      "% between LOQ and LL samples": 59.23,
      "% Above LL samples": 4.46,
      Conventional: 59.23,
    },
    {
      "Organic flag": "Organic",
      "Food commodity": "Fruits and tree nuts",
      "% between LOQ and LL samples": 17.22,
      "% Above LL samples": 0.67,
      Organic: 17.22,
    },
    {
      "Organic flag": "Conventional",
      "Food commodity": "Other plant products",
      "% between LOQ and LL samples": 26.52,
      "% Above LL samples": 7.55,
      Conventional: 26.52,
    },
    {
      "Organic flag": "Organic",
      "Food commodity": "Other plant products",
      "% between LOQ and LL samples": 22.61,
      "% Above LL samples": 2.04,
      Organic: 22.61,
    },
    {
      "Organic flag": "Conventional",
      "Food commodity": "Vegetables",
      "% between LOQ and LL samples": 41.32,
      "% Above LL samples": 3.82,
      Conventional: 41.32,
    },
    {
      "Organic flag": "Organic",
      "Food commodity": "Vegetables",
      "% between LOQ and LL samples": 19.74,
      "% Above LL samples": 0.51,
      Organic: 19.74,
    },
  ];

  mapped.forEach((d) => {
    if (d["Organic flag"] === "Organic") {
      d["Organic"] =
        props.comparisonFilter === 0
          ? d["% between LOQ and LL samples"]
          : d["% Above LL samples"];
    } else {
      d["Conventional"] =
        props.comparisonFilter === 0
          ? d["% between LOQ and LL samples"]
          : d["% Above LL samples"];
    }
  });

  // Sort the mapped data by 'Food commodity'
  const sortedMapped = mapped.sort((a, b) =>
    a["Food commodity"].localeCompare(b["Food commodity"])
  );

  // Initialize DataSet and create a DataView
  const ds = new DataSet();
  const dv = ds.createView().source(sortedMapped);
  dv.transform({
    type: "fold",
    fields: ["Conventional", "Organic"],
    key: "type",
    value: "value",
  });

  const [canvas, setCanvas] = useState(undefined);

  const scale = { value: { min: 0, max: 80 } };

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={{ paddingRight: { xs: "40px", sm: "30px", md: "0px" } }}
      >
        <p
          className="titleChart"
          style={{
            marginBottom: "1.1rem",
            fontFamily: "Roboto Condensed, sans-serif",
            width: "100%",
          }}
        >
          {props.title}
        </p>
        <DownloadButton canvas={canvas} fileName={"Chart"} csv={mapped} />
      </Box>
      <Chart
        height={300}
        data={dv}
        autoFit
        onGetG2Instance={(chartInstance) => {
          setTimeout(() => {
            setCanvas(chartInstance.canvas);
          }, 1000);
        }}
        scale={scale}
        padding={[15, 50, 60, 185]}
      >
        <Legend />
        <Coordinate transpose scale={[1, -1]} />
        <Axis
          name="Food commodity"
          label={{
            offset: 12,
            style: {
              fontFamily: "Roboto Condensed, sans-serif",
            },
          }}
        />
        <Axis
          label={{
            formatter: (a) => `${a}%`,
          }}
          name="value"
          position={"right"}
        />
        <Tooltip shared>
          {(title, items) => {
            // Filter the mapped data for the current Food commodity
            const filtered = mapped.filter(
              (d) => d["Food commodity"] === title
            );

            if (filtered.length < 2) {
              return (
                <div
                  id="tooltipWrap"
                  style={{
                    padding: "5px 10px 10px 5px",
                    width: "auto",
                    color: "#22294D",
                    fontFamily: "Roboto Condensed",
                    opacity: "1",
                  }}
                >
                  <div
                    className="g2-tooltip-title"
                    style={{
                      fontSize: "medium",
                      paddingBottom: "0",
                      fontWeight: "bold",
                    }}
                  >
                    {title}
                  </div>
                  <ul className="g2-tooltip-list">
                    <li>No data available.</li>
                  </ul>
                </div>
              );
            }

            // Determine which entry is 'Conventional' and which is 'Organic'
            const conventionalEntry = filtered.find(
              (d) => d["Organic flag"] === "Conventional"
            );
            const organicEntry = filtered.find(
              (d) => d["Organic flag"] === "Organic"
            );

            return (
              <div
                id="tooltipWrap"
                style={{
                  padding: "5px 10px 10px 5px",
                  width: "auto",
                  color: "#22294D",
                  fontFamily: "Roboto Condensed",
                  opacity: "1",
                }}
              >
                <div
                  className="g2-tooltip-title"
                  style={{
                    fontSize: "medium",
                    paddingBottom: "0",
                    fontWeight: "bold",
                  }}
                >
                  {title}
                </div>
                <ul className="g2-tooltip-list">
                  {conventionalEntry && (
                    <li style={{ padding: "2px 0" }} data-index="0">
                      <span
                        style={{
                          backgroundColor: "#22294D",
                          width: "8px",
                          height: "8px",
                          borderRadius: "50%",
                          display: "inline-block",
                          marginRight: "8px",
                        }}
                      />
                      Conventional:{" "}
                      {Math.round(conventionalEntry["Conventional"] * 10) / 10 + "%"}
                    </li>
                  )}
                  {organicEntry && (
                    <li style={{ padding: "2px 0" }} data-index="1">
                      <span
                        style={{
                          backgroundColor: "#90B1CE",
                          width: "8px",
                          height: "8px",
                          borderRadius: "50%",
                          display: "inline-block",
                          marginRight: "8px",
                        }}
                      />
                      Organic:{" "}
                      {Math.round(organicEntry["Organic"] * 10) / 10 + "%"}
                    </li>
                  )}
                </ul>
              </div>
            );
          }}
        </Tooltip>
        <Interval
          position="Food commodity*value"
          color={["type", ["#22294D", "#90B1CE"]]}
          adjust={[
            {
              type: "dodge",
              marginRatio: 1 / 32,
            },
          ]}
        />
      </Chart>
      <Box
        pr={3}
        pt={3}
        display="flex"
        justifyContent={{ xs: "flex-start", sm: "center", md: "center" }}
        alignItems="center"
        flexDirection={{ xs: "column", sm: "row", md: "row" }}
        gap={2}
      >
        <Box
          component="div"
          display="inline"
          className="dodgebar-note"
          width="180px"
        >
          <p>
            <b>Copper (RD)</b>
          </p>
          <p>
            <b>654</b> Quantified
            <br />
            <b>3</b> Above legal limit
          </p>
        </Box>
        <Box
          component="div"
          className="dodgebar-note"
          display="inline"
          width="180px"
        >
          <p>
            <b>Bromide Ion (RD)</b>
          </p>
          <p>
            <b>91</b> Quantified
            <br />
            <b>0</b> Above legal limit
          </p>
        </Box>
        <Box
          component="div"
          className="dodgebar-note"
          display="inline"
          width="180px"
        >
          <p>
            <b>Chlorate (RD)</b>
          </p>
          <p>
            <b>75</b> Quantified
            <br />
            <b>4</b> Above legal limit
          </p>
        </Box>
      </Box>
    </Box>
  );
};

export default DodgeBarChart;
